/**
 * `safeJsonParse(raw, fallback[, guard])` — `JSON.parse` that returns a
 * fallback instead of throwing. For STORED JSON (`line_items`, incident
 * categories, `party_members`, `meal_selections`, localStorage drafts and
 * caches) where a corrupt value must degrade to "empty", never blank a
 * screen or wedge a flow.
 *
 * Measured 2026-09-07 (helper-canon Task 5): 60 `JSON.parse` sites under
 * components/, hooks/, lib/, app/ — 55 were private try/catch-with-fallback
 * copies (three of them named: `parseJsonArray`, `parseLineItems`,
 * `parsePayload`) or bare parses that threw straight into a render.
 *
 * Contract:
 *   - `raw` that is not a non-empty string → `fallback` (null, undefined,
 *     "", and — at runtime — any non-string, since a stored column is a
 *     string or nothing).
 *   - Invalid JSON → `fallback`. Never throws, for any input.
 *   - With a `guard`, a parsed value the guard rejects → `fallback`; so the
 *     caller's consumer can rely on the shape, not just on "it parsed".
 *   - The JSON literal `"null"` parses to `null`: without a guard that is
 *     the returned value (a caller typed `T | null` wants exactly that);
 *     with a guard, `null` fails every guard below and yields `fallback`.
 *
 * Guards: `isStringArray` (ids, categories, table numbers), `isRecord`
 * (an object payload such as `meal_selections`), `isObjectArray` (a list
 * of records such as `party_members`). `Array.isArray` also fits the
 * `guard` slot when only the container matters.
 *
 * NOT for request bodies: a malformed API body is a 400, so routes keep
 * `JSON.parse` / `request.json()` and their own error handling — the
 * ESLint nudge (`belvedere/prefer-safe-json-parse`, `warn`) is off for
 * `app/api/**` and for this file, which holds the one sanctioned parse.
 */
export function safeJsonParse<T>(
  raw: string | null | undefined,
  fallback: T,
  guard?: (value: unknown) => value is T,
): T {
  if (typeof raw !== "string" || raw === "") return fallback
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return fallback
  }
  if (guard && !guard(parsed)) return fallback
  return parsed as T
}

/** Every element is a string — ids, categories, table numbers. */
export function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === "string")
}

/** A non-null, non-array object — an object payload such as `meal_selections`. */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

/**
 * Every element is a non-null, non-array object — a list of records such as
 * `party_members` or `line_items`. Checks the container and the element
 * kind; the element KEYS are trusted the same way `Array.isArray`'s
 * `any[]` trusts them, so type `T` to the row shape the writer persists.
 */
export function isObjectArray<T extends object = Record<string, unknown>>(value: unknown): value is T[] {
  return Array.isArray(value) && value.every(isRecord)
}
